import React, { useRef, useEffect } from "react";
import {
  PhoneXMarkIcon,
  MicrophoneIcon,
  SpeakerXMarkIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { useCall } from "../../contexts/CallContext";
import useRingingSound from "../../hooks/useRingingSound";

const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

const ActiveCallUI = () => {
  const {
    callStatus,
    callerInfo,
    calleeDisplayName,
    isMuted,
    callDuration,
    endCall,
    toggleMute,
    remoteStream,
  } = useCall();

  const audioRef = useRef(null);

  useEffect(() => {
    if (audioRef.current && remoteStream) {
      audioRef.current.srcObject = remoteStream;
      
    }
  }, [remoteStream, callStatus]);

  const isCalling = callStatus === "requesting";
  const isOngoing = callStatus === "ongoing";

  useRingingSound(isCalling);

  if (!isCalling && !isOngoing) return null;

  const displayName =
    callerInfo
      ? [callerInfo.firstName, callerInfo.lastName].filter(Boolean).join(" ").trim() ||
        callerInfo.username ||
        "Call"
      : calleeDisplayName || "Connecting...";

  const avatarUrl = callerInfo?.agentDetails?.agentImage;

  return (
    <div className="fixed inset-0 z-50 bg-gray-900 bg-opacity-95 flex flex-col items-center justify-center">
      <audio ref={audioRef} autoPlay playsInline />
      <div className="text-center mb-8">
        <div className="w-24 h-24 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={displayName}
              className="w-20 h-20 rounded-full object-cover"
            />
          ) : (
            <UserIcon className="h-12 w-12 text-gray-400" />
          )}
        </div>
        <h2 className="text-2xl font-semibold text-white mb-1">
          {displayName}
        </h2>

        {isCalling && (
          <p className="text-lg text-gray-400 animate-pulse">Calling...</p>
        )}

        {isOngoing && (
          <p className="text-4xl font-mono text-green-400 font-medium">
            {formatDuration(callDuration)}
          </p>
        )}
      </div>

      <div className="flex items-center space-x-8">
        {isOngoing && (
          <button
            onClick={toggleMute}
            className={`p-5 rounded-full transition-colors ${
              isMuted
                ? "bg-yellow-500 hover:bg-yellow-600 text-white"
                : "bg-gray-700 hover:bg-gray-600 text-white"
            }`}
            title={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? (
              <SpeakerXMarkIcon className="h-8 w-8" />
            ) : (
              <MicrophoneIcon className="h-8 w-8" />
            )}
          </button>
        )}

        <button
          onClick={endCall}
          className="p-5 rounded-full bg-red-600 hover:bg-red-700 text-white transition-colors"
          title={isCalling ? "Cancel" : "End call"}
        >
          <PhoneXMarkIcon className="h-8 w-8" />
        </button>
      </div>

      {isOngoing && (
        <p className="text-gray-400 text-sm mt-8">
          {isMuted ? "You are muted" : "Tap to mute"}
        </p>
      )}
    </div>
  );
};

export default ActiveCallUI;
